import { LinkField } from '@prismicio/types'
import { getLinkFieldUrl } from '../../data/prismic-helper'
import { useState, useEffect, useRef } from 'react'
import { useMuteState } from '../../util/muteState'
import { MediaControl } from '../atoms/MediaControl/MediaControl'
import { useGlobalScrollPercentage } from '../../util/scrollPercentage'

export interface AudioPlayerProps {
  audioFile: LinkField
  audioType:
    | 'None'
    // Ambient is disabled currently in prismic since there are issues on mobile with it
    | 'Ambient (Global Sound Toggle)'
    | 'Audiostory (Progress Bar Visible)'
  audioVolume: number | null
  lang: string
}

export default function AudioPlayer(props: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const { muted, setMuted } = useMuteState()
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const scrollPercentage = useGlobalScrollPercentage()

  // Set initial mute state
  useEffect(() => {
    if (props.audioType === 'Ambient (Global Sound Toggle)') {
      setMuted(true)
      setPlaying(false)
    }

    if (props.audioType === 'Audiostory (Progress Bar Visible)') {
      setMuted(false)
      setPlaying(true)
    }
  }, [props.audioType, setMuted, setPlaying])

  // Forward playing and muting state to audio element
  useEffect(() => {
    if (!audioRef.current) {
      return
    }

    audioRef.current.muted = muted

    if (playing) {
      audioRef.current.play().catch((error) => {
        console.error('Error controlling audio:', error)
        setPlaying(false)
      })
    } else {
      audioRef.current.pause()
    }
  }, [playing, muted, audioRef])

  // Update volume of AudioElement
  useEffect(() => {
    if (audioRef.current && !muted) {
      audioRef.current.volume = props.audioVolume ? props.audioVolume / 100 : 0.75
    }
  }, [muted, props.audioVolume, audioRef])

  // Update progress of MediaControls
  useEffect(() => {
    const audioElement = audioRef.current
    if (!audioElement) {
      return
    }

    const updateProgress = () => {
      if (audioElement.duration) {
        const currentProgress = (audioElement.currentTime / audioElement.duration) * 100
        setProgress(currentProgress)
      }
    }

    const handleEnded = () => {
      if (!isLoop(props.audioType)) {
        setPlaying(false)
      }
    }

    audioElement.addEventListener('timeupdate', updateProgress)
    audioElement.addEventListener('loadedmetadata', () => {
      if (audioElement) {
        setDuration(audioElement.duration)
      }
    })
    audioElement.addEventListener('ended', handleEnded)

    return () => {
      if (audioElement) {
        audioElement.removeEventListener('timeupdate', updateProgress)
        audioElement.removeEventListener('loadedmetadata', () => {})
        audioElement.removeEventListener('ended', handleEnded)
      }
    }
  }, [props.audioType, audioRef])

  // Add media key event listeners (when OS play/pause events happen)
  useEffect(() => {
    const handleMediaKeyEvent = (event: { action: MediaSessionAction }) => {
      if (event.action === 'play') {
        if (props.audioType === 'Ambient (Global Sound Toggle)') {
          setMuted(false)
        }
        setPlaying(true)
      } else if (event.action === 'pause') {
        if (props.audioType === 'Ambient (Global Sound Toggle)') {
          setMuted(true)
        }
        setPlaying(false)
      }
    }

    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', handleMediaKeyEvent)
      navigator.mediaSession.setActionHandler('pause', handleMediaKeyEvent)
    }

    return () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null)
        navigator.mediaSession.setActionHandler('pause', null)
      }
    }
  }, [props.audioType, setMuted, setPlaying])

  const link = getLinkFieldUrl(props.audioFile)

  if (!link || !props.audioType || props.audioType === 'None') {
    return null
  }

  const toggleMute = () => {
    const newMuteState = !muted
    setMuted(newMuteState)

    if (props.audioType === 'Ambient (Global Sound Toggle)' && audioRef.current) {
      if (newMuteState) {
        setPlaying(false)
      } else {
        setPlaying(true)
      }
    }
  }

  const handleProgressClick = (progress: number) => {
    if (!audioRef.current || !duration) {
      return
    }

    // First ensure audio is playing
    setPlaying(true)
    // Then set the new time position
    const newTime = (progress / 100) * duration
    audioRef.current.currentTime = newTime
    setProgress(progress)
  }

  const isLoop = (audioType: string): boolean => {
    return audioType === 'Ambient (Global Sound Toggle)'
  }

  return (
    <>
      <audio
        src={link}
        loop={isLoop(props.audioType)}
        ref={audioRef}
      />
      <MediaControl
        playing={playing}
        progress={progress}
        muted={muted}
        onPlayPause={() => setPlaying(!playing)}
        onProgressClick={handleProgressClick}
        onMuteToggle={toggleMute}
        position="fixed"
        mediaURL={link}
        subtitleLang={props.lang}
        duration={duration}
        controls={{
          playPause: props.audioType === 'Audiostory (Progress Bar Visible)',
          progress: props.audioType === 'Audiostory (Progress Bar Visible)',
          mute: true,
        }}
        scrollPercentage={props.audioType === 'Audiostory (Progress Bar Visible)' ? scrollPercentage : null}
      />
    </>
  )
}
